import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { phoneContactApi } from "@bw/shared/api";
import type { PhoneVerificationStartResponse, SmsConsentStatusResponse } from "@bw/shared/types/generated";
import EmojiEventsRounded from "@mui/icons-material/EmojiEventsRounded";
import PaymentsRounded from "@mui/icons-material/PaymentsRounded";
import SchoolRounded from "@mui/icons-material/SchoolRounded";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Page } from "../../../common/components/Page";
import { queryKeys } from "../../../common/query/queryClient";
import { visuallyHidden } from "../../../common/visuallyHidden";

const E164 = /^\+[1-9][0-9]{1,14}$/;
type SmsPreferences = {
  payments: boolean;
  classes: boolean;
  competitions: boolean;
};
const DEFAULT_SMS_PREFERENCES: SmsPreferences = {
  payments: true,
  classes: false,
  competitions: false,
};
type SmsPreferenceKey = keyof typeof DEFAULT_SMS_PREFERENCES;

function smsPreferences(
  value: Partial<SmsPreferences> | null | undefined,
): SmsPreferences {
  return { ...DEFAULT_SMS_PREFERENCES, ...value };
}

/** Private contact setup and an optional, independent transactional-SMS choice. */
export function NotificationSettingsPage() {
  const queryClient = useQueryClient();
  const phone = useQuery({
    queryKey: queryKeys.me.phone(),
    queryFn: () => phoneContactApi.getStatus(),
    retry: false,
  });
  const consent = useQuery({
    queryKey: queryKeys.me.smsConsent(),
    queryFn: () => phoneContactApi.getSmsConsent(),
    retry: false,
  });
  const start = useMutation({ mutationFn: phoneContactApi.start });
  const confirm = useMutation({ mutationFn: phoneContactApi.confirm });
  const remove = useMutation({ mutationFn: phoneContactApi.remove });
  const choice = useMutation({ mutationFn: phoneContactApi.setSmsConsent });
  const [changingPhone, setChangingPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [challenge, setChallenge] = useState<PhoneVerificationStartResponse | null>(null);
  const [awaitingConsentRefresh, setAwaitingConsentRefresh] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const submissionBusy = useRef(false);
  const startAttempt = useRef<{ phone: string; key: string } | null>(null);
  const hasPhone = phone.data?.has_verified_phone === true;
  const showPhoneForm = !hasPhone || changingPhone;

  async function handleStart() {
    if (submissionBusy.current || !E164.test(phoneInput) || start.isPending) return;
    submissionBusy.current = true;
    try {
      if (startAttempt.current?.phone !== phoneInput) {
        startAttempt.current = { phone: phoneInput, key: globalThis.crypto.randomUUID() };
      }
      const result = await start.mutateAsync({
        phone_e164: phoneInput,
        idempotency_key: startAttempt.current.key,
      });
      startAttempt.current = null;
      setChallenge(result);
      setCodeInput("");
      setPhoneInput("");
    } catch {
      // Mutation state renders the server error without exposing the number.
    } finally {
      submissionBusy.current = false;
    }
  }

  async function handleConfirm() {
    if (!challenge || !/^[0-9]{6}$/.test(codeInput) || confirm.isPending) return;
    try {
      const verified = await confirm.mutateAsync({ challenge_id: challenge.challenge_id, code: codeInput });
      setAwaitingConsentRefresh(true);
      queryClient.setQueryData(queryKeys.me.phone(), verified);
      queryClient.setQueryData<SmsConsentStatusResponse>(queryKeys.me.smsConsent(), {
        enabled: false,
        preferences: DEFAULT_SMS_PREFERENCES,
        disclosure_version: consent.data?.disclosure_version ?? "2026-10-27",
        masked_phone: verified.masked_phone,
        changed_at: null,
      });
      setCodeInput("");
      setChallenge(null);
      setChangingPhone(false);
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.me.phone() }),
          queryClient.invalidateQueries({ queryKey: queryKeys.me.smsConsent() }),
        ]);
      } finally {
        setAwaitingConsentRefresh(false);
      }
    } catch {
      // Keep the challenge for a corrected code; never infer verification.
    }
  }

  async function handleSmsChoice(
    enabled: boolean,
    preferences: SmsPreferences = smsPreferences(consent.data?.preferences),
  ) {
    if (!hasPhone || !consent.data || choice.isPending || awaitingConsentRefresh) return;
    try {
      const saved = await choice.mutateAsync({
        enabled,
        preferences,
        disclosure_version: consent.data.disclosure_version,
        idempotency_key: globalThis.crypto.randomUUID(),
      });
      queryClient.setQueryData(queryKeys.me.smsConsent(), saved);
    } catch {
      // The server remains the source of truth; the switch does not flip.
    }
  }

  async function handlePreference(key: SmsPreferenceKey, enabled: boolean) {
    if (!consent.data?.enabled) return;
    await handleSmsChoice(true, {
      ...smsPreferences(consent.data.preferences),
      [key]: enabled,
    });
  }

  async function handleRemovePhone() {
    if (!hasPhone || remove.isPending) return;
    try {
      const empty = await remove.mutateAsync();
      queryClient.setQueryData(queryKeys.me.phone(), empty);
      queryClient.setQueryData<SmsConsentStatusResponse>(queryKeys.me.smsConsent(), {
        enabled: false,
        preferences: DEFAULT_SMS_PREFERENCES,
        disclosure_version: consent.data?.disclosure_version ?? "2026-10-27",
        masked_phone: null,
        changed_at: null,
      });
      setChallenge(null);
      setCodeInput("");
      setPhoneInput("");
      setChangingPhone(false);
      setRemoveDialogOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.me.phone() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.me.smsConsent() }),
      ]);
    } catch {
      // Keep the verified contact visible until the server confirms removal.
    }
  }

  return (
    <Page>
      <Stack spacing={3} sx={{ maxWidth: 680 }}>
        <Stack spacing={0.5} useFlexGap>
          <Typography component="h1" sx={{ fontSize: "1.375rem", fontWeight: 700 }}>
            Notification settings
          </Typography>
          <Typography color="text.secondary">
            Manage your verified phone and choose which account updates arrive by text.
          </Typography>
        </Stack>
        {phone.isLoading ? (
          <Typography role="status" color="text.secondary">Loading phone settings…</Typography>
        ) : phone.isError ? (
          <Alert severity="error">Text-message settings are unavailable. Try again later.</Alert>
        ) : (
          <Stack spacing={1.5} component="section" aria-label="Verified phone">
            <Typography component="h2" variant="h6">Phone number</Typography>
            {hasPhone && !changingPhone ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                }}
              >
                <Stack spacing={0.25}>
                  <Typography sx={{ fontWeight: 600 }}>{phone.data?.masked_phone}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Verified for optional account texts
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="text"
                    aria-label="Change phone"
                    onClick={() => setChangingPhone(true)}
                  >
                    Change
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    onClick={() => setRemoveDialogOpen(true)}
                  >
                    Remove
                  </Button>
                </Stack>
              </Stack>
            ) : null}
            {showPhoneForm ? (
              <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
                <TextField
                  label="Phone number"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+12065550123"
                  value={phoneInput}
                  onChange={(event) => {
                    setPhoneInput(event.target.value);
                    if (startAttempt.current?.phone !== event.target.value) startAttempt.current = null;
                  }}
                  size="small"
                  slotProps={{ htmlInput: { maxLength: 16 } }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => void handleStart()}
                  disabled={!E164.test(phoneInput) || start.isPending}
                >
                  {start.isPending ? "Requesting…" : "Send verification code"}
                </Button>
                {start.isError ? (
                  <Alert severity="error">
                    {start.error instanceof Error && "status" in start.error && start.error.status === 429
                      ? "Too many attempts. Please try again later."
                      : "We couldn’t request a code. Please try again."}
                  </Alert>
                ) : null}
              </Stack>
            ) : null}
            {challenge ? (
              <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
                <Typography color="text.secondary">
                  Enter the code for {challenge.masked_phone}.
                  {challenge.delivery_status === "uncertain"
                    ? " Delivery could not be confirmed; request a new code if none arrives."
                    : ""}
                </Typography>
                <TextField
                  label="Verification code"
                  value={codeInput}
                  onChange={(event) => setCodeInput(event.target.value)}
                  slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 6 } }}
                  autoComplete="one-time-code"
                  size="small"
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => void handleConfirm()}
                  disabled={!/^[0-9]{6}$/.test(codeInput) || confirm.isPending}
                >
                  {confirm.isPending ? "Verifying…" : "Confirm code"}
                </Button>
                {confirm.isError ? (
                  <Alert severity="error">Invalid or expired code. Please try again.</Alert>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        )}

        <Stack spacing={1.5} component="section" aria-label="Transactional texts">
          <Typography component="h2" variant="h6">Transactional texts</Typography>
          <Typography color="text.secondary">
            Choose which time-sensitive account updates arrive by text. Message
            frequency varies; message and data rates may apply. Reply STOP to opt
            out or HELP for assistance, or contact support@badmintonwarriors.com.
            Texts are optional and never required to purchase.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            By turning on transactional texts, you agree to receive these service
            messages. Read our{" "}
            <Link component={RouterLink} to="/sms-terms">Transactional SMS Terms</Link>
            {" "}and{" "}
            <Link component={RouterLink} to="/privacy">Privacy Policy</Link>.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(hasPhone && !awaitingConsentRefresh && consent.data?.enabled)}
                disabled={!hasPhone || phone.isError || consent.isLoading || consent.isError || choice.isPending || awaitingConsentRefresh}
                onChange={(_, checked) => void handleSmsChoice(checked)}
                slotProps={{ input: { "aria-label": "Transactional text messages" } }}
              />
            }
            label="Transactional text messages"
          />
          <Stack spacing={1.25} aria-label="Text message categories">
            {[
              {
                key: "payments" as const,
                title: "Payments",
                detail: "Confirmations, refunds, failed payments and renewal actions.",
                icon: <PaymentsRounded color="primary" aria-hidden="true" />,
              },
              {
                key: "classes" as const,
                title: "Classes",
                detail: "Enrollments, reminders, cancellations and schedule changes.",
                icon: <SchoolRounded color="primary" aria-hidden="true" />,
              },
              {
                key: "competitions" as const,
                title: "Tournaments & leagues",
                detail: "Registration, deadlines, schedule, venue and match updates.",
                icon: <EmojiEventsRounded color="primary" aria-hidden="true" />,
              },
            ].map((item) => (
              <Paper key={item.key} variant="outlined" sx={{ p: 1.75, borderRadius: 2.5 }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                  {item.icon}
                  <Stack spacing={0.25} sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 650 }}>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.detail}
                    </Typography>
                  </Stack>
                  <Switch
                    checked={smsPreferences(consent.data?.preferences)[item.key]}
                    disabled={!hasPhone || !consent.data?.enabled || choice.isPending || awaitingConsentRefresh}
                    onChange={(_, checked) => void handlePreference(item.key, checked)}
                    slotProps={{ input: { "aria-label": `${item.title} text messages` } }}
                  />
                </Stack>
              </Paper>
            ))}
          </Stack>
          {choice.isError ? (
            <Alert severity="error">We couldn’t save your text-message choice. Try again.</Alert>
          ) : null}
          {consent.isError && !phone.isError ? (
            <Alert severity="error">We couldn’t load your text-message choice.</Alert>
          ) : null}
        </Stack>
      </Stack>
      <Dialog
        open={removeDialogOpen}
        onClose={() => { if (!remove.isPending) setRemoveDialogOpen(false); }}
        aria-labelledby="remove-phone-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="remove-phone-title">Remove verified phone?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You’ll stop receiving transactional texts. You can verify a phone
            again later, but you’ll need to turn texts back on separately.
          </DialogContentText>
          {remove.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              We couldn’t remove your phone. Try again.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)} disabled={remove.isPending}>
            Keep phone
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleRemovePhone()}
            disabled={remove.isPending}
          >
            {remove.isPending ? "Removing…" : "Remove phone"}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
